import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SearchBar from '../components/SearchBar';
import ExportMenu from '../components/ExportMenu';

const CLIENT_TYPES = ['Income Tax', 'GST & PT', 'Business', 'Consulting'];

export default function ClientsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (typeFilter) params.set('client_type', typeFilter);
      const { data } = await api.get(`/clients?${params}`);
      setClients(data.clients || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, [search, typeFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted');
      fetchClients();
    } catch {
      toast.error('Failed to delete client');
    }
  };

  return (
    <div data-testid="clients-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">Clients</h1>
          <p className="text-sm text-[#4F734F] mt-1">{total} clients registered</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportMenu
            endpoint="/exports/clients"
            params={{ ...(search ? { search } : {}), ...(typeFilter ? { client_type: typeFilter } : {}) }}
            filename="clients"
            testIdPrefix="clients"
          />
          <Link to="/admin/clients/new">
            <Button className="btn-accent gap-2" data-testid="add-client-btn">
              <Plus size={16} /> Add Client
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by name, company, email, GST..."
          testId="client-search-input"
          minWidth="min-w-[320px]"
        />
        <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-48 h-11 input-field" data-testid="client-type-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {CLIENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E6EDE6] rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Company</th>
                <th>Contact</th>
                <th>GST Number</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8 text-olive-400">Loading...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-olive-400">No clients found</td></tr>
              ) : clients.map(client => (
                <tr key={client._id} data-testid={`client-row-${client._id}`}>
                  <td className="font-medium text-[#111B11]">{client.full_name}</td>
                  <td>{client.company_name || '-'}</td>
                  <td>{client.contact_number || client.email || '-'}</td>
                  <td className="font-mono text-xs">{client.gst_number || '-'}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(client.client_types || []).map(t => (
                        <span key={t} className="bg-olive-100 text-olive-700 rounded-full px-2 py-0.5 text-xs">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => navigate(`/admin/clients/${client._id}`)} className="p-1.5 hover:bg-olive-50 rounded-sm" data-testid={`view-client-${client._id}`}>
                        <Eye size={14} className="text-olive-500" />
                      </button>
                      <button onClick={() => navigate(`/admin/clients/${client._id}/edit`)} className="p-1.5 hover:bg-olive-50 rounded-sm" data-testid={`edit-client-${client._id}`}>
                        <Pencil size={14} className="text-olive-500" />
                      </button>
                      {user?.role === 'admin' && (
                        <button onClick={() => handleDelete(client._id)} className="p-1.5 hover:bg-red-50 rounded-sm" data-testid={`delete-client-${client._id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
